import { Inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { removeItem, patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';

import { WINDOW } from '@ng-web-apis/common';
import { ApiEventsService } from '../services';
import { SnackbarService } from '@fitscovery/ui/snackbar';

import { EventsControlState } from './events-control-state';
import { EventsStateModel, Event, EventResponse, EventsResponse, EventAttendeeResponse, EventAttendee, AddEventAttendeesResponse } from '../models';
import { EventsActions } from '../actions';

export const eventStateDefaults: EventsStateModel = {
	events: null,
  selectedEvent: null,
	selectedEventAttendees: null,
  bookingResponse: null,
	loaded: false
};

export const EVENT_STATE_TOKEN = new StateToken<EventsStateModel>('events');

@State<EventsStateModel>({
	name: EVENT_STATE_TOKEN,
	defaults: eventStateDefaults
})

@Injectable()
export class EventsState extends EventsControlState implements NgxsOnInit {

	@Selector()
	static events(state: EventsStateModel): Event[] {
		return state.events!;
	}

	@Selector()
	static selectedEvent(state: EventsStateModel): Event {
		return state.selectedEvent!;
	}

	@Selector()
	static selectedEventAttendees(state: EventsStateModel): EventAttendee[] {
		return state.selectedEventAttendees!;
	}

	constructor(
    @Inject(WINDOW) window: any,
    private eventService: ApiEventsService,
    snackbar: SnackbarService
  ) {
    super(window, snackbar);
  }

	ngxsOnInit(): void {
	}

  @Action(EventsActions.GetEventById)
	GetEventById(ctx: StateContext<EventsStateModel>, action: EventsActions.GetEventById) {
    return this.eventService.getEventById(action.eventId).pipe(
      this.catchResponse,
      tap((response: EventResponse) => ctx.patchState({ selectedEvent: response.data[0] }))
    );
	}

  @Action(EventsActions.GetEventsBySubdomain)
	getEventsBySubdomain(ctx: StateContext<EventsStateModel>, action: EventsActions.GetEventsBySubdomain) {
    return this.eventService.getEventsBySubdomain(action.subdomain).pipe(
      tap((response: EventsResponse) => ctx.patchState({ events: response.data, loaded: true })),
      this.catchResponse
    );
	}

  @Action(EventsActions.CreateEvent)
	createEvent(ctx: StateContext<EventsStateModel>, action: EventsActions.CreateEvent) {
    return this.eventService.createEvent(action.event).pipe(
      this.catchResponse
    );
  }

  @Action(EventsActions.UpdateEvent)
	updateEvent(ctx: StateContext<EventsStateModel>, action: EventsActions.UpdateEvent) {
    return this.eventService.updateEvent(action.event).pipe(
      this.catchResponse
    );
	}

  @Action(EventsActions.DuplicateEvent)
	duplicateEvent(ctx: StateContext<EventsStateModel>, action: EventsActions.UpdateEvent) {
    return this.eventService.duplicateEvent(action.event).pipe(
      this.catchResponse
    );
	}

  @Action(EventsActions.DeleteEvent)
	deleteEvent(ctx: StateContext<EventsStateModel>, action: EventsActions.DeleteEvent) {
    return this.eventService.deleteEvent(action.eventId, action.partnerId).pipe(
      tap(() => ctx.setState(patch({
        events: removeItem((event: unknown) => (event as Event).eventId === action.eventId) as any
      }))),
      this.catchResponse
    );
	}
  
  @Action(EventsActions.GetAttendeesWithIdByEventId)
	getAttendeesWithIdByEventId(ctx: StateContext<EventsStateModel>, action: EventsActions.GetAttendeesWithIdByEventId) {
    return this.eventService.getAttendeesWithIdByEventId(action.eventId).pipe(
      tap((response: EventAttendeeResponse) => ctx.patchState({ selectedEventAttendees: response.data })),
      this.catchResponse
    );
	}

  @Action(EventsActions.AddEventAttendees)
	addEventAttendees(ctx: StateContext<EventsStateModel>, action: EventsActions.AddEventAttendees) {
    return this.eventService.addEventAttendees(action.eventId, action.userList).pipe(
      this.catchResponse
    );
	}

  @Action(EventsActions.UpdateEventAttendee)
	updateEventAttendee(ctx: StateContext<EventsStateModel>, { userId, partnerId, eventId, isPresent }: EventsActions.UpdateEventAttendee) {
    return this.eventService.updateEventAttendee(userId, partnerId, eventId, isPresent).pipe(
      tap(() => ctx.dispatch(new EventsActions.GetAttendeesWithIdByEventId(eventId))),
      delay(500),
      this.updateEventAttendeeResponse,
      this.catchResponse
    );
	}
  
  @Action(EventsActions.UpdateAllEventAttendee)
	updateAllEventAttendee(ctx: StateContext<EventsStateModel>, { partnerId, eventId, isPresent }: EventsActions.UpdateAllEventAttendee) {
    return this.eventService.updateAllEventAttendee(partnerId, eventId, isPresent).pipe(
      tap(() => ctx.dispatch(new EventsActions.GetAttendeesWithIdByEventId(eventId))),
      delay(500),
      this.updateAllEventAttendeeResponse,
      this.catchResponse
    );
	}

  @Action(EventsActions.DownloadCsvEventAttendees)
	downloadCsvEventAttendees(ctx: StateContext<EventsStateModel>, action: EventsActions.DownloadCsvEventAttendees) {
    return this.eventService.downloadCsvEventAttendees(action.eventId, action.partnerId).pipe(
      this.downloadCsvEventAttendeesResponse,
      this.catchResponse
    );
	}

  @Action(EventsActions.BookNow)
  bookNow(ctx: StateContext<EventsStateModel>, action: EventsActions.BookNow) {
    return this.eventService.bookNow(action.eventId, action.quantity).pipe(
      tap((bookingResponse: EventAttendeeResponse) => ctx.patchState({ bookingResponse })),
      catchError((response: HttpErrorResponse) => {
        ctx.patchState({ bookingResponse: response.error });
        return of(response.error as EventAttendeeResponse);
      })
    );
  }

  @Action(EventsActions.SoftReserve)
  softReserve(ctx: StateContext<EventsStateModel>, action: EventsActions.SoftReserve) {
    return this.eventService.softReserve(action.eventId).pipe(
      this.catchResponse
    );
  }

  @Action(EventsActions.CancelFreeEventSlot)
  cancelFreeEventSlot(ctx: StateContext<EventsStateModel>, action: EventsActions.CancelFreeEventSlot) {
    return this.eventService.cancelFreeEventSlot(action.eventId).pipe(
      tap((bookingResponse: EventAttendeeResponse) => ctx.patchState({ bookingResponse })),
      catchError((response: HttpErrorResponse) => {
        ctx.patchState({ bookingResponse: response.error });
        return of(response.error as EventAttendeeResponse);
      })
    );
  }

  @Action(EventsActions.CancelEventSlot)
  cancelEventSlot(ctx: StateContext<EventsStateModel>, action: EventsActions.CancelEventSlot) {
    return this.eventService.cancelEventSlot(action.eventId).pipe(
      tap((bookingResponse: EventAttendeeResponse) => ctx.patchState({ bookingResponse })),
      catchError((response: HttpErrorResponse) => {
        ctx.patchState({ bookingResponse: response.error });
        return of(response.error as EventAttendeeResponse);
      })
    );
  }

  @Action(EventsActions.RemoveSelectedEvent)
	removeSelectedEvent(ctx: StateContext<EventsStateModel>) {
    ctx.patchState({ selectedEvent: null });
	}

  @Action(EventsActions.RemoveSelectedEventAttendees)
	removeSelectedEventAttendees(ctx: StateContext<EventsStateModel>) {
    ctx.patchState({ selectedEventAttendees: null });
	}

}
